import type { NavEntry, NavGroup, NavItem } from './components/SiteChrome';
import { moduleOrigins, type ModuleOrigins } from './modules';

/**
 * Uma entrada do mapa do site. `repeat` marca uma pagina que ja esta noutro
 * grupo (Torneios em Agenda e em Competicao): aparece nos dois, mas so o grupo
 * principal fica marcado como atual.
 */
export type SiteNavLink = NavItem & { repeat?: boolean };
export type SiteNavGroup = { label: string; items: SiteNavLink[] };
export type SiteNavEntry = SiteNavLink | SiteNavGroup;

export type SiteNavOptions = {
  /** Ha premios recebidos publicados. */
  awards?: boolean;
  /** Ha homenagens publicadas. */
  memorial?: boolean;
  /** Ha parceiros publicados. */
  partners?: boolean;
};

const isGroup = (entry: SiteNavEntry | NavEntry): entry is SiteNavGroup | NavGroup =>
  'items' in entry;

/**
 * O mapa do site (requisitos-novos/mapa-do-site.md): a mesma definicao para o
 * cabecalho e o rodape, no site e no Stream Portal. Dentro do site as ligacoes
 * ficam relativas; nos outros modulos levam o endereco do site. Prémios,
 * Parceiros e Em memória so aparecem quando ha conteudo publicado.
 */
export function siteNav(
  current: 'site' | 'streaming',
  origins: ModuleOrigins = moduleOrigins(),
  { awards = false, memorial = false, partners = false }: SiteNavOptions = {},
): SiteNavEntry[] {
  const site = (path: string) => (current === 'site' ? path : `${origins.site}${path}`);
  const link = (label: string, path: string, repeat = false): SiteNavLink =>
    repeat ? { label, href: site(path), repeat } : { label, href: site(path) };
  return [
    link('Notícias', '/noticias'),
    {
      label: 'Agenda',
      items: [
        link('Calendário', '/calendario'),
        link('Eventos', '/eventos'),
        link('Torneios', '/torneios', true),
        link('Passatempos', '/passatempos'),
      ],
    },
    {
      label: 'Competição',
      items: [
        link('Equipas', '/equipas'),
        link('Torneios', '/torneios'),
        link('Hall of Fame', '/hall-of-fame'),
        link('Equipas antigas', '/equipas/antigas'),
      ],
    },
    {
      label: 'Comunidade',
      items: [
        link('Membros', '/membros'),
        link('Streamers', '/streamers'),
        link('Arena', '/arena'),
        link('Ser sócio', '/socios'),
      ],
    },
    {
      label: 'Grow uP',
      items: [
        link('Sobre nós', '/sobre'),
        ...(awards ? [link('Prémios', '/premios')] : []),
        ...(partners ? [link('Parceiros', '/parceiros')] : []),
        ...(memorial ? [link('Em memória', '/memorial')] : []),
        link('Contactos', '/contactos'),
      ],
    },
    link('Loja', '/loja'),
  ];
}

/**
 * Marca a pagina aberta: a ligacao cujo caminho e o mais comprido prefixo do
 * endereco (`/equipas/antigas` ganha a `/equipas`), e o grupo que a contem,
 * exceto quando ali e so repeticao.
 */
export function markCurrentNav(entries: SiteNavEntry[], pathname: string): NavEntry[] {
  const path = pathname.replace(/\/+$/, '') || '/';
  const pathOf = (href: string) => (href.startsWith('/') ? href : new URL(href).pathname);
  const matches = (href: string) => {
    const p = pathOf(href);
    return path === p || path.startsWith(`${p}/`);
  };
  const all = entries.flatMap((e) => (isGroup(e) ? e.items : [e]));
  const best = all
    .filter((l) => matches(l.href))
    .reduce<string | null>(
      (acc, l) => (acc === null || pathOf(l.href).length > acc.length ? pathOf(l.href) : acc),
      null,
    );
  const isCurrent = (l: SiteNavLink) => best !== null && pathOf(l.href) === best;
  const plain = ({ label, href }: SiteNavLink, current: boolean): NavItem => ({
    label,
    href,
    current,
  });
  return entries.map((entry) => {
    if (!isGroup(entry)) return plain(entry, isCurrent(entry));
    return {
      label: entry.label,
      current: entry.items.some((l) => isCurrent(l) && !l.repeat),
      items: entry.items.map((l) => plain(l, isCurrent(l))),
    };
  });
}
